import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useMutation } from '@apollo/client/react';

import {
    CreateLocationDocument,
    DeleteLocationsDocument,
    IBasicFileFragment,
    ILocationFragment,
    UpdateLocationDocument,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { getImageId } from 'common/utils/image';
import { LocationForm } from 'organisation/components/LocationForm';
import { ILocationFormValues } from 'organisation/types';
import { evictMeetingModulesByLocationIds } from 'training/utils/module';

interface IProps {
    location?: ILocationFragment;
    onClose?(): void;
    onCreate?(location: ILocationFragment): void;
}

export const LocationFormController = ({
    location,
    onClose,
    onCreate,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const client = useApolloClient();

    const [createLocation] = useMutation(CreateLocationDocument);
    const [updateLocation] = useMutation(UpdateLocationDocument);
    const [deleteLocations] = useMutation(DeleteLocationsDocument);

    const isEdit = !!location;

    const initialValues: ILocationFormValues = {
        title: location?.title || '',
        address: location?.location || '',
        description: location?.description || '',
        image: location?.image || null,
    };

    const handleCreate: SubmitHandler<ILocationFormValues> = async (values) => {
        const { title, address, description, image } = values;

        let createdLocation: ILocationFragment | undefined;

        try {
            const response = await createLocation({
                variables: {
                    location: {
                        title,
                        location: address,
                        description,
                        imageId: image?.id,
                    },
                },
            });

            createdLocation = response.data?.createLocation?.location;
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (!createdLocation) return;

        client.refetchQueries({ include: ['Locations'] });
        client.cache.evict({ fieldName: 'paginatedLocations' });
        client.cache.gc();

        displaySnackbar(
            translate('locationsManagementPage.actionSuccess.create'),
            { variant: 'success' }
        );

        onCreate?.(createdLocation);
        onClose?.();
    };

    const handleUpdate: SubmitHandler<ILocationFormValues> = async (values) => {
        if (!location) return;

        const { image: newImage, address, ...other } = values;
        const imageId = getImageId(
            location.image as IBasicFileFragment | undefined,
            newImage
        );

        try {
            await updateLocation({
                variables: {
                    id: location.id,
                    location: {
                        ...other,
                        location: address,
                        imageId,
                    },
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        client.cache.evict({ fieldName: 'paginatedLocations' });
        client.cache.gc();

        displaySnackbar(
            translate('locationsManagementPage.actionSuccess.update'),
            { variant: 'success' }
        );

        onClose?.();
    };

    const handleDelete = async () => {
        if (!location) return;

        let succeededIds: string[] = [];
        let failedIds: string[] = [];

        try {
            const response = await deleteLocations({
                variables: { ids: [location.id] },
                update: (cache, result) => {
                    const ids =
                        result?.data?.deleteLocations?.bulkDelete
                            ?.succeededIds || [];
                    if (!ids.length) return;

                    cache.evict({ fieldName: 'locations' });
                    cache.evict({ fieldName: 'paginatedLocations' });
                    evictMeetingModulesByLocationIds(cache, ids);
                    cache.gc();
                },
            });

            const { bulkDelete } = response.data?.deleteLocations || {};
            succeededIds = bulkDelete?.succeededIds || [];
            failedIds = bulkDelete?.failedIds || [];
        } catch (error) {
            showApolloError(error);

            return;
        }

        if (failedIds.length || !succeededIds.length) {
            displaySnackbar(
                translate(
                    'locationsManagementPage.actionSuccess.bulkDeleteFailed',
                    { count: failedIds.length || 1 }
                ),
                { variant: 'error' }
            );

            return;
        }

        displaySnackbar(
            translate('locationsManagementPage.actionSuccess.bulkDelete', {
                count: succeededIds.length,
            }),
            { variant: 'success' }
        );

        onClose?.();
    };

    return (
        <LocationForm
            initialValues={initialValues}
            isEdit={isEdit}
            onClose={onClose}
            onDelete={isEdit ? handleDelete : undefined}
            onSubmit={isEdit ? handleUpdate : handleCreate}
        />
    );
};
